import React, { useState, useEffect } from 'react';
import { clientsApi } from '../services/api';

interface Cliente {
  id: number;
  nombre: string;
  email: string;
  activo: boolean;
}

interface ClientesSelectorComponentProps {
  selectedClients: number[];
  onClientesChange: (clientes: number[]) => void;
}

const ClientesSelectorComponent: React.FC<ClientesSelectorComponentProps> = ({ 
  selectedClients = [], 
  onClientesChange 
}) => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClientes();
  }, []);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const response = await clientsApi.getAll();
      if (response.data.success) {
        // Solo clientes activos
        const clientesActivos = response.data.data.filter((c: Cliente) => c.activo);
        setClientes(clientesActivos);
      }
    } catch (error) {
      console.error('Error cargando clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClientes = clientes.filter(cliente =>
    cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClienteToggle = (clienteId: number) => {
    const newSelected = selectedClients.includes(clienteId)
      ? selectedClients.filter(id => id !== clienteId)
      : [...selectedClients, clienteId];
    
    onClientesChange(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClientes.length) {
      // Deseleccionar todos
      onClientesChange([]);
    } else {
      // Seleccionar todos los filtrados
      onClientesChange(filteredClientes.map(c => c.id));
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '1rem' }}>
        <div className="spinner" />
        <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
          Cargando clientes...
        </p>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: '0.5rem', padding: '1rem' }}>
      {/* Búsqueda */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar clientes..."
          className="form-input"
          style={{ marginBottom: '0.5rem' }}
        />
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSelectAll}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer'
            }}
          >
            {selectedClients.length === filteredClientes.length ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
          </button>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {selectedClients.length} de {filteredClientes.length} seleccionados
          </span>
        </div>
      </div>

      {/* Lista de clientes */}
      <div style={{ 
        maxHeight: '200px', 
        overflowY: 'auto',
        border: '1px solid #e5e7eb',
        borderRadius: '0.25rem',
        padding: '0.5rem'
      }}>
        {filteredClientes.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280', margin: '1rem 0' }}>
            No se encontraron clientes
          </p>
        ) : (
          filteredClientes.map(cliente => (
            <div
              key={cliente.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0.5rem',
                borderBottom: '1px solid #f3f4f6',
                ':last-child': { borderBottom: 'none' }
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', width: '100%', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedClients.includes(cliente.id)}
                  onChange={() => handleClienteToggle(cliente.id)}
                  style={{ marginRight: '0.5rem' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                    {cliente.nombre}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    ID: {cliente.id} • {cliente.email}
                  </div>
                </div>
              </label>
            </div>
          ))
        )}
      </div>

      {selectedClients.length > 0 && (
        <div style={{ 
          marginTop: '1rem', 
          padding: '0.75rem', 
          backgroundColor: '#f0fdf4', 
          borderRadius: '0.25rem',
          border: '1px solid #bbf7d0'
        }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '500', color: '#166534', marginBottom: '0.5rem' }}>
            Clientes Seleccionados:
          </div>
          <div style={{ fontSize: '0.75rem', color: '#16a34a' }}>
            {filteredClientes
              .filter(c => selectedClients.includes(c.id))
              .map(c => c.nombre)
              .join(', ')
            }
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientesSelectorComponent;